import { z } from "zod";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, PositiveIntegerSchema, SourceRefSchema, UnknownRecordSchema } from "./source.js";
export const SPEC_POLICY_FLAGS_SCHEMA_VERSION = "spec-policy-flags-schema-v3";
export const SPEC_POLICY_FLAG_CATALOG_VERSION = "spec-policy-flag-catalog-v3";
export const SPEC_POLICY_FLAG_STANDARD_DEFAULT_LABEL = "default — no trigger found in spec";
export const SPEC_POLICY_FLAG_ORG_DEFAULT_LABEL = "default — no trigger found in spec; org standard default applied";
export const SPEC_POLICY_FLAG_FAMILY_IDS = [
    "control-topology.central-screen-mode",
    "sensor-policy.supply-air",
    "sensor-policy.return-air",
    "item-class-scope.scope",
    "netmaster-capability.remote-app-control",
    "netmaster-capability.modbus",
    "netmaster-capability.time-scheduling",
    "netmaster-capability.holiday-mode",
    "netmaster-capability.central-control",
    "netmaster-capability.io-module-mapping",
    "netmaster-capability.central-panel-drives-actuators",
    "network-power.poe-switch-inclusion",
    "ufh-policy.loop-count-default",
    "product-generation.era",
    "control-topology.local-interface-product",
    "ufh-policy.zone-type",
    "ufh-policy.limit-sensor-redundancy",
    "network-power.central-panel-enclosure-inclusion",
    "network-power.metering-integration",
    "sensor-policy.district-interface-units",
    "controller-policy.fcu-mode",
    "supply-scope.ufh-actuator-supply",
    "supply-scope.fcu-actuator-supply",
    "supply-scope.riser-cabling-supply",
    "interface-demand.hiu-interface",
    "interface-demand.ciu-interface",
    "interface-demand.electric-ufh-zone-interface",
    "interface-demand.towel-rail-interface",
    "interface-demand.wet-ufh-actuator-interface",
    "netmaster-capability.no-trigger-posture"
];
export const SpecPolicyFlagFamilyIdSchema = z.enum(SPEC_POLICY_FLAG_FAMILY_IDS);
export const SpecPolicyFlagProvenanceSchema = z.enum([
    "ai-extracted",
    "user-override",
    "user-decision",
    "standard-default",
    "org-default",
    "provider-conflict"
]);
export const SpecPolicyFlagSilencePostureSchema = z.enum([
    "labelled-default-ok",
    "derivable-requires-evidence"
]);
export const SpecPolicyFlagDomainSchema = z.enum([
    "control-topology",
    "sensor-policy",
    "item-class-scope",
    "netmaster-capability",
    "network-power",
    "ufh-policy",
    "product-generation",
    "controller-policy",
    "supply-scope",
    "interface-demand"
]);
const SpecPolicyFlagFamilyDomains = {
    "control-topology.central-screen-mode": "control-topology",
    "sensor-policy.supply-air": "sensor-policy",
    "sensor-policy.return-air": "sensor-policy",
    "item-class-scope.scope": "item-class-scope",
    "netmaster-capability.remote-app-control": "netmaster-capability",
    "netmaster-capability.modbus": "netmaster-capability",
    "netmaster-capability.time-scheduling": "netmaster-capability",
    "netmaster-capability.holiday-mode": "netmaster-capability",
    "netmaster-capability.central-control": "netmaster-capability",
    "netmaster-capability.io-module-mapping": "netmaster-capability",
    "netmaster-capability.central-panel-drives-actuators": "netmaster-capability",
    "network-power.poe-switch-inclusion": "network-power",
    "ufh-policy.loop-count-default": "ufh-policy",
    "product-generation.era": "product-generation",
    "control-topology.local-interface-product": "control-topology",
    "ufh-policy.zone-type": "ufh-policy",
    "ufh-policy.limit-sensor-redundancy": "ufh-policy",
    "network-power.central-panel-enclosure-inclusion": "network-power",
    "network-power.metering-integration": "network-power",
    "sensor-policy.district-interface-units": "sensor-policy",
    "controller-policy.fcu-mode": "controller-policy",
    "supply-scope.ufh-actuator-supply": "supply-scope",
    "supply-scope.fcu-actuator-supply": "supply-scope",
    "supply-scope.riser-cabling-supply": "supply-scope",
    "interface-demand.hiu-interface": "interface-demand",
    "interface-demand.ciu-interface": "interface-demand",
    "interface-demand.electric-ufh-zone-interface": "interface-demand",
    "interface-demand.towel-rail-interface": "interface-demand",
    "interface-demand.wet-ufh-actuator-interface": "interface-demand",
    "netmaster-capability.no-trigger-posture": "netmaster-capability"
};
export const SpecPolicyFlagScopeLevelSchema = z.enum([
    "project",
    "requirement",
    "apartment-type",
    "room",
    "item-class"
]);
const ProjectFlagScopeSchema = z
    .object({
    level: z.literal("project"),
    projectId: IdSchema
})
    .strict();
const RequirementFlagScopeSchema = z
    .object({
    level: z.literal("requirement"),
    projectId: IdSchema,
    requirementId: IdSchema
})
    .strict();
const ApartmentTypeFlagScopeSchema = z
    .object({
    level: z.literal("apartment-type"),
    projectId: IdSchema,
    apartmentTypeId: IdSchema
})
    .strict();
const RoomFlagScopeSchema = z
    .object({
    level: z.literal("room"),
    projectId: IdSchema,
    apartmentTypeId: IdSchema,
    roomRef: NonEmptyStringSchema
})
    .strict();
const ItemClassFlagScopeSchema = z
    .object({
    level: z.literal("item-class"),
    projectId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    itemClass: NonEmptyStringSchema
})
    .strict();
export const SpecPolicyFlagScopeSchema = z.discriminatedUnion("level", [
    ProjectFlagScopeSchema,
    RequirementFlagScopeSchema,
    ApartmentTypeFlagScopeSchema,
    RoomFlagScopeSchema,
    ItemClassFlagScopeSchema
]);
export const SpecPolicyFlagConfidenceSchema = z
    .object({
    score: z.number().min(0).max(1),
    label: z.enum(["high", "medium", "low"])
})
    .strict();
export const SpecPolicyFlagBooleanValueSchema = z
    .object({
    kind: z.literal("boolean"),
    value: z.boolean()
})
    .strict();
export const SpecPolicyFlagModeValueSchema = z
    .object({
    kind: z.literal("mode"),
    mode: NonEmptyStringSchema
})
    .strict();
export const SpecPolicyFlagIntegerValueSchema = z
    .object({
    kind: z.literal("integer"),
    value: z.number().int(),
    unit: NonEmptyStringSchema.optional()
})
    .strict();
export const SpecPolicyFlagRangeValueSchema = z
    .object({
    kind: z.literal("range"),
    min: z.number(),
    max: z.number(),
    unit: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.max < value.min) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "range max must be greater than or equal to min",
            path: ["max"]
        });
    }
});
export const SpecPolicyFlagListValueSchema = z
    .object({
    kind: z.literal("list"),
    values: z.array(NonEmptyStringSchema)
})
    .strict();
/** @deprecated Persisted v2 values are accepted by `SpecPolicyFlagValueSchema`
 * and normalized to one of the closed structured variants below. */
export const SpecPolicyFlagObjectValueSchema = z
    .object({
    kind: z.literal("object"),
    value: UnknownRecordSchema,
    objectType: NonEmptyStringSchema.optional()
})
    .strict();
export const SpecPolicyFlagUfhLoopCountDefaultValueSchema = z
    .object({
    kind: z.literal("structured"),
    objectType: z.literal("UfhLoopCountDefault"),
    smallAreaLoops: z.literal(1),
    largeAreaMin: z.literal(2),
    largeAreaMax: z.literal(3),
    largeAreaTrigger: z.enum([
        "source_area_threshold",
        "room_kind_standard",
        "user_default_profile",
        "needs_user_decision"
    ])
})
    .strict();
export const SpecPolicyFlagUfhZoneTypeValueSchema = z
    .object({
    kind: z.literal("structured"),
    objectType: z.literal("UfhZoneType"),
    overall: z.enum(["wet", "electric", "mixed", "none", "needs_user_decision"]),
    bathroom: z.enum(["wet", "electric", "none", "needs_user_decision"]).optional()
})
    .strict();
export const SpecPolicyFlagMeteringIntegrationValueSchema = z
    .object({
    kind: z.literal("structured"),
    objectType: z.literal("MeteringIntegration"),
    included: z.boolean(),
    protocol: z.enum([
        "bacnet",
        "modbus",
        "mbus",
        "wireless_mbus",
        "pulse",
        "mix",
        "other",
        "none"
    ])
})
    .strict();
export const SpecPolicyFlagDistrictInterfaceUnitsValueSchema = z
    .object({
    kind: z.literal("structured"),
    objectType: z.literal("DistrictInterfaceUnits"),
    hiu: z.boolean(),
    ciu: z.boolean()
})
    .strict();
/**
 * The only structured policy values that flow through the catalog and
 * consumers. `objectType` is the discriminant; every branch is closed so an
 * LLM cannot invent a free-form record. The direct fields also avoid spending
 * an emitted-schema nesting level on the old `{ kind: "object", value: ... }`
 * wrapper (and flatten the UFH loop bounds at the same time).
 */
export const SpecPolicyFlagStructuredValueSchema = z.discriminatedUnion("objectType", [
    SpecPolicyFlagUfhLoopCountDefaultValueSchema,
    SpecPolicyFlagUfhZoneTypeValueSchema,
    SpecPolicyFlagMeteringIntegrationValueSchema,
    SpecPolicyFlagDistrictInterfaceUnitsValueSchema
]);
// These schemas describe the retired v2 payloads inside the `{ kind: "object";
// value: ... }` wrapper. They are intentionally strict too: migration must not
// turn an unrecognized payload property into a silently discarded v3 value.
const SpecPolicyFlagV2UfhLoopCountDefaultPayloadSchema = z
    .object({
    smallAreaLoops: z.literal(1),
    largeAreaLoops: z
        .object({
        min: z.literal(2),
        max: z.literal(3)
    })
        .strict(),
    largeAreaTrigger: z.enum([
        "source_area_threshold",
        "room_kind_standard",
        "user_default_profile",
        "needs_user_decision"
    ])
})
    .strict();
const SpecPolicyFlagV2UfhZoneTypePayloadSchema = z
    .object({
    overall: z.enum(["wet", "electric", "mixed", "none", "needs_user_decision"]),
    bathroom: z.enum(["wet", "electric", "none", "needs_user_decision"]).optional()
})
    .strict();
const SpecPolicyFlagV2MeteringIntegrationPayloadSchema = z
    .object({
    included: z.boolean(),
    protocol: z.enum([
        "bacnet",
        "modbus",
        "mbus",
        "wireless_mbus",
        "pulse",
        "mix",
        "other",
        "none"
    ])
})
    .strict();
const SpecPolicyFlagV2DistrictInterfaceUnitsPayloadSchema = z
    .object({
    hiu: z.boolean(),
    ciu: z.boolean()
})
    .strict();
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const migrateLegacyStructuredPolicyFlagValue = (objectType, payload) => {
    if (objectType === "UfhLoopCountDefault" ||
        (objectType === undefined &&
            "smallAreaLoops" in payload &&
            isRecord(payload.largeAreaLoops))) {
        const parsed = SpecPolicyFlagV2UfhLoopCountDefaultPayloadSchema.safeParse(payload);
        if (!parsed.success) {
            return undefined;
        }
        return {
            kind: "structured",
            objectType: "UfhLoopCountDefault",
            smallAreaLoops: parsed.data.smallAreaLoops,
            largeAreaMin: parsed.data.largeAreaLoops.min,
            largeAreaMax: parsed.data.largeAreaLoops.max,
            largeAreaTrigger: parsed.data.largeAreaTrigger
        };
    }
    if (objectType === "UfhZoneType" ||
        (objectType === undefined && "overall" in payload)) {
        const parsed = SpecPolicyFlagV2UfhZoneTypePayloadSchema.safeParse(payload);
        if (!parsed.success) {
            return undefined;
        }
        return {
            kind: "structured",
            objectType: "UfhZoneType",
            overall: parsed.data.overall,
            ...(parsed.data.bathroom === undefined ? {} : { bathroom: parsed.data.bathroom })
        };
    }
    if (objectType === "MeteringIntegration" ||
        (objectType === undefined && "included" in payload && "protocol" in payload)) {
        const parsed = SpecPolicyFlagV2MeteringIntegrationPayloadSchema.safeParse(payload);
        if (!parsed.success) {
            return undefined;
        }
        return {
            kind: "structured",
            objectType: "MeteringIntegration",
            included: parsed.data.included,
            protocol: parsed.data.protocol
        };
    }
    if (objectType === "DistrictInterfaceUnits" ||
        (objectType === undefined && "hiu" in payload && "ciu" in payload)) {
        const parsed = SpecPolicyFlagV2DistrictInterfaceUnitsPayloadSchema.safeParse(payload);
        if (!parsed.success) {
            return undefined;
        }
        return {
            kind: "structured",
            objectType: "DistrictInterfaceUnits",
            hiu: parsed.data.hiu,
            ciu: parsed.data.ciu
        };
    }
    return undefined;
};
/** Normalize v2 persisted object values before the closed v3 parser runs. */
export const migrateSpecPolicyFlagValue = (value) => {
    if (!isRecord(value)) {
        return value;
    }
    if (value.kind === "object") {
        const legacyValue = SpecPolicyFlagObjectValueSchema.safeParse(value);
        if (!legacyValue.success) {
            return undefined;
        }
        return migrateLegacyStructuredPolicyFlagValue(legacyValue.data.objectType, legacyValue.data.value);
    }
    if ((value.kind === "integer" || value.kind === "range") && value.unit === null) {
        const { unit: _unit, ...withoutNullUnit } = value;
        return withoutNullUnit;
    }
    if (value.kind === "structured" &&
        value.objectType === "UfhZoneType" &&
        value.bathroom === null) {
        const { bathroom: _bathroom, ...withoutNullBathroom } = value;
        return withoutNullBathroom;
    }
    return value;
};
export const SpecPolicyFlagValueSchema = z.preprocess(migrateSpecPolicyFlagValue, z.union([
    SpecPolicyFlagBooleanValueSchema,
    SpecPolicyFlagModeValueSchema,
    SpecPolicyFlagIntegerValueSchema,
    SpecPolicyFlagRangeValueSchema,
    SpecPolicyFlagListValueSchema,
    SpecPolicyFlagStructuredValueSchema
]));
const modeValueSchema = (values) => z
    .object({
    kind: z.literal("mode"),
    mode: z.enum(values)
})
    .strict();
export const CENTRAL_SCREEN_TOPOLOGY_MODE_VALUES = [
    "central_screen_additive",
    "central_screen_is_stat_for_own_zone"
];
const CentralScreenModeValueSchema = modeValueSchema(CENTRAL_SCREEN_TOPOLOGY_MODE_VALUES);
const SensorPolicyValueSchema = modeValueSchema([
    "include_per_fcu",
    "exclude_unless_specified",
    "by_others",
    "needs_user_decision"
]);
const ItemClassScopeValueSchema = modeValueSchema([
    "include",
    "exclude_from_bom",
    "by_others",
    "commission_only",
    "install_only",
    "price_zero_with_note"
]);
const PoeSwitchInclusionValueSchema = modeValueSchema([
    "include_when_screens_present",
    "explicit_by_others",
    "not_required",
    "needs_user_decision"
]);
const ProductGenerationEraValueSchema = modeValueSchema([
    "current_catalog",
    "legacy_fcu_503_refit"
]);
const LocalInterfaceProductValueSchema = modeValueSchema([
    "ts4",
    "ts7",
    "sensor_only",
    "rdu",
    "none"
]);
const CentralPanelEnclosureInclusionValueSchema = modeValueSchema([
    "include",
    "explicit_by_others",
    "not_required",
    "needs_user_decision"
]);
const FcuModeValueSchema = modeValueSchema([
    "heating_cooling",
    "cool_only",
    "heat_only",
    "needs_user_decision"
]);
const NoTriggerPostureValueSchema = modeValueSchema([
    "advisory_lean_include",
    "advisory_lean_exclude",
    "needs_user_decision"
]);
const SpecPolicyFamilyValueSchemas = {
    "control-topology.central-screen-mode": CentralScreenModeValueSchema,
    "sensor-policy.supply-air": SensorPolicyValueSchema,
    "sensor-policy.return-air": SensorPolicyValueSchema,
    "item-class-scope.scope": ItemClassScopeValueSchema,
    "netmaster-capability.remote-app-control": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.modbus": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.time-scheduling": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.holiday-mode": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.central-control": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.io-module-mapping": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.central-panel-drives-actuators": SpecPolicyFlagBooleanValueSchema,
    "network-power.poe-switch-inclusion": PoeSwitchInclusionValueSchema,
    "ufh-policy.loop-count-default": SpecPolicyFlagUfhLoopCountDefaultValueSchema,
    "product-generation.era": ProductGenerationEraValueSchema,
    "control-topology.local-interface-product": LocalInterfaceProductValueSchema,
    "ufh-policy.zone-type": SpecPolicyFlagUfhZoneTypeValueSchema,
    "ufh-policy.limit-sensor-redundancy": SpecPolicyFlagBooleanValueSchema,
    "network-power.central-panel-enclosure-inclusion": CentralPanelEnclosureInclusionValueSchema,
    "network-power.metering-integration": SpecPolicyFlagMeteringIntegrationValueSchema,
    "sensor-policy.district-interface-units": SpecPolicyFlagDistrictInterfaceUnitsValueSchema,
    "controller-policy.fcu-mode": FcuModeValueSchema,
    "supply-scope.ufh-actuator-supply": SpecPolicyFlagBooleanValueSchema,
    "supply-scope.fcu-actuator-supply": SpecPolicyFlagBooleanValueSchema,
    "supply-scope.riser-cabling-supply": SpecPolicyFlagBooleanValueSchema,
    "interface-demand.hiu-interface": SpecPolicyFlagBooleanValueSchema,
    "interface-demand.ciu-interface": SpecPolicyFlagBooleanValueSchema,
    "interface-demand.electric-ufh-zone-interface": SpecPolicyFlagBooleanValueSchema,
    "interface-demand.towel-rail-interface": SpecPolicyFlagBooleanValueSchema,
    "interface-demand.wet-ufh-actuator-interface": SpecPolicyFlagBooleanValueSchema,
    "netmaster-capability.no-trigger-posture": NoTriggerPostureValueSchema
};
/**
 * Every catalog family must declare its silence posture explicitly. This is a
 * total `Record` (not `Partial`) so TypeScript itself enforces completeness:
 * adding a new family id to `SPEC_POLICY_FLAG_FAMILY_IDS` without adding a
 * posture here is a compile error. See `_build/F2-FLAGS-CONTRACT.md`
 * (silencePosture classification table) for the one-line WHY per family.
 *
 * - "derivable-requires-evidence": tender silence may only resolve to the
 *   family's declared silence-safe value (see
 *   `SpecPolicyFamilySilenceSafeValues` below); any other concrete value
 *   requires evidence-backed ai-extracted provenance or user-decision
 *   provenance. Reserved for families where equipment/feature presence is
 *   already established but a further product-changing fork (which the
 *   downstream BOM/topology genuinely forks on) cannot be safely guessed.
 * - "labelled-default-ok": a concrete standard-default value is allowed on
 *   tender silence, but every standard-default current value and history entry
 *   must carry a visible "default - no trigger found" style label (enforced by
 *   `addLabelledDefaultProvenanceLabelIssues` below).
 */
const SpecPolicyFamilySilencePostures = {
    "control-topology.central-screen-mode": "labelled-default-ok",
    "sensor-policy.supply-air": "labelled-default-ok",
    "sensor-policy.return-air": "labelled-default-ok",
    "item-class-scope.scope": "labelled-default-ok",
    "netmaster-capability.remote-app-control": "labelled-default-ok",
    "netmaster-capability.modbus": "labelled-default-ok",
    "netmaster-capability.time-scheduling": "labelled-default-ok",
    "netmaster-capability.holiday-mode": "labelled-default-ok",
    "netmaster-capability.central-control": "labelled-default-ok",
    "netmaster-capability.io-module-mapping": "labelled-default-ok",
    "netmaster-capability.central-panel-drives-actuators": "labelled-default-ok",
    "network-power.poe-switch-inclusion": "labelled-default-ok",
    "ufh-policy.loop-count-default": "labelled-default-ok",
    "product-generation.era": "labelled-default-ok",
    "control-topology.local-interface-product": "labelled-default-ok",
    "ufh-policy.zone-type": "derivable-requires-evidence",
    "ufh-policy.limit-sensor-redundancy": "labelled-default-ok",
    "network-power.central-panel-enclosure-inclusion": "labelled-default-ok",
    "network-power.metering-integration": "labelled-default-ok",
    "sensor-policy.district-interface-units": "labelled-default-ok",
    "controller-policy.fcu-mode": "derivable-requires-evidence",
    "supply-scope.ufh-actuator-supply": "labelled-default-ok",
    "supply-scope.fcu-actuator-supply": "labelled-default-ok",
    "supply-scope.riser-cabling-supply": "labelled-default-ok",
    "interface-demand.hiu-interface": "labelled-default-ok",
    "interface-demand.ciu-interface": "labelled-default-ok",
    "interface-demand.electric-ufh-zone-interface": "labelled-default-ok",
    "interface-demand.towel-rail-interface": "labelled-default-ok",
    "interface-demand.wet-ufh-actuator-interface": "labelled-default-ok",
    "netmaster-capability.no-trigger-posture": "labelled-default-ok"
};
export const getSpecPolicyFlagSilencePosture = (familyId) => SpecPolicyFamilySilencePostures[familyId];
/**
 * The one value each "derivable-requires-evidence" family may resolve to on
 * tender silence without evidence or a user decision (fcu-mode's
 * `needs_user_decision`, generalized to any value shape). Any other concrete
 * value for that family requires evidence-backed ai-extracted provenance or
 * user-decision provenance. Keyed only by the families that carry the
 * "derivable-requires-evidence" posture above.
 */
const SpecPolicyFamilySilenceSafeValues = {
    "controller-policy.fcu-mode": { kind: "mode", mode: "needs_user_decision" },
    "ufh-policy.zone-type": {
        kind: "structured",
        objectType: "UfhZoneType",
        overall: "needs_user_decision"
    }
};
const stableUnknownStringify = (value) => {
    if (Array.isArray(value)) {
        return `[${value.map((entry) => stableUnknownStringify(entry)).join(",")}]`;
    }
    if (value !== null && typeof value === "object") {
        const record = value;
        return `{${Object.keys(record)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableUnknownStringify(record[key])}`)
            .join(",")}}`;
    }
    return JSON.stringify(value);
};
export const validateSpecPolicyFlagValueForFamily = (familyId, value) => {
    const familySchema = SpecPolicyFamilyValueSchemas[familyId];
    const result = familySchema.safeParse(value);
    if (result.success) {
        return [];
    }
    return result.error.issues.map((issue) => {
        const path = issue.path.length > 0 ? `${issue.path.join(".")}: ` : "";
        return `${path}${issue.message}`;
    });
};
const addFamilyValueIssues = (ctx, familyId, value, path) => {
    for (const message of validateSpecPolicyFlagValueForFamily(familyId, value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path
        });
    }
};
const requireEvidenceForNonDefault = (ctx, provenance, evidenceRefs, path) => {
    if (provenance === "standard-default" ||
        provenance === "org-default" ||
        provenance === "user-decision") {
        return;
    }
    if ((evidenceRefs?.length ?? 0) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "ai-extracted, user-override, and provider-conflict policy flags require evidenceRefs",
            path
        });
    }
};
/**
 * Generalized fix2 mechanism (was `isConcreteFcuModeValue`, forked to
 * fcu-mode only). Any family carrying the "derivable-requires-evidence"
 * posture is checked the same way fcu-mode always was: a value is "unsafe on
 * silence" when the family is derivable-requires-evidence AND the candidate
 * value differs from that family's declared silence-safe value. A family
 * with no declared safe value is unsafe for every value (defensive: a family
 * must not be marked derivable-requires-evidence without pinning what
 * silence may resolve to).
 */
const isSilenceUnsafeValueForDerivableFamily = (familyId, value) => {
    if (getSpecPolicyFlagSilencePosture(familyId) !== "derivable-requires-evidence") {
        return false;
    }
    const safeValue = SpecPolicyFamilySilenceSafeValues[familyId];
    if (safeValue === undefined) {
        return true;
    }
    return stableUnknownStringify(value) !== stableUnknownStringify(safeValue);
};
/**
 * Which sub-path an issue about a family's value should point at. Mode-kind
 * values point at `.mode` (unchanged fcu-mode behavior); structured values
 * point at their meaningful discriminated field; everything else falls back
 * to `.kind`.
 */
const derivableIssuePathSegment = (value) => {
    switch (value.kind) {
        case "mode":
            return "mode";
        case "structured":
            return value.objectType === "UfhZoneType" ? "overall" : "objectType";
        default:
            return "kind";
    }
};
const hasEvidenceBackedAiProvenance = (provenance, evidenceRefs, why) => provenance === "ai-extracted" &&
    (evidenceRefs?.length ?? 0) > 0 &&
    why.trim().length > 0;
const hasUserDecisionProvenance = (provenance) => provenance === "user-decision";
const addDerivableConcreteValueProvenanceIssues = (ctx, familyId, value, provenance, evidenceRefs, why, path) => {
    if (!isSilenceUnsafeValueForDerivableFamily(familyId, value)) {
        return;
    }
    if (hasEvidenceBackedAiProvenance(provenance, evidenceRefs, why) ||
        hasUserDecisionProvenance(provenance)) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${familyId} concrete values require ai-extracted provenance with evidenceRefs and why, or user-decision provenance`,
        path
    });
};
const addDerivableDefaultValueIssues = (ctx, familyId, value, path) => {
    if (!isSilenceUnsafeValueForDerivableFamily(familyId, value)) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${familyId} tender silence may only default to its declared silence-safe value; concrete values require evidence-backed or user-decision provenance`,
        path
    });
};
/**
 * Recognized phrasings for the Q15 binding labelling directive ("must
 * explicitly say `default - no trigger found in spec` when that is the
 * reason"). Kept as a marker set rather than one literal string so existing
 * equivalent phrasings already in use across the F2 chain ("NO MENTION ->
 * standard-default applied", "Default applied - no source evidence.") remain
 * valid without a forced rewrite; new callers should prefer the canonical
 * phrase (markers[0]).
 */
const DEFAULT_PROVENANCE_LABEL_MARKERS = [
    "default — no trigger found",
    "default - no trigger found",
    "no trigger found",
    "standard-default applied",
    "default applied",
    "no source evidence",
    "no mention"
];
const hasDefaultProvenanceLabel = (why) => {
    const normalizedWhy = why.toLowerCase();
    return DEFAULT_PROVENANCE_LABEL_MARKERS.some((marker) => normalizedWhy.includes(marker));
};
/**
 * The "labelled-default-ok" half of the posture contract: a standard-default
 * current value or history entry for a labelled-default-ok family must visibly
 * say it is a default with no trigger found, matching Rich's Q15 binding
 * labelling directive.
 */
const addLabelledDefaultProvenanceLabelIssues = (ctx, familyId, provenance, why, path) => {
    if (provenance !== "standard-default") {
        return;
    }
    if (getSpecPolicyFlagSilencePosture(familyId) !== "labelled-default-ok") {
        return;
    }
    if (hasDefaultProvenanceLabel(why)) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${familyId} standard-default values must carry a "default - no trigger found" style label in why`,
        path
    });
};
const SpecPolicyFlagConflictCandidateSchema = z
    .object({
    value: SpecPolicyFlagValueSchema,
    evidenceRefs: z.array(SourceRefSchema).min(1),
    why: NonEmptyStringSchema,
    confidence: SpecPolicyFlagConfidenceSchema
})
    .strict();
export const SpecPolicyFlagConflictSchema = z
    .object({
    id: IdSchema,
    familyId: SpecPolicyFlagFamilyIdSchema,
    scope: SpecPolicyFlagScopeSchema,
    reason: NonEmptyStringSchema,
    candidateValues: z.array(SpecPolicyFlagConflictCandidateSchema).min(1),
    recommendedValue: SpecPolicyFlagValueSchema.optional(),
    needsUserDecision: z.boolean()
})
    .strict()
    .superRefine((value, ctx) => {
    value.candidateValues.forEach((candidate, index) => {
        addFamilyValueIssues(ctx, value.familyId, candidate.value, [
            "candidateValues",
            index,
            "value"
        ]);
    });
    if (value.recommendedValue !== undefined) {
        addFamilyValueIssues(ctx, value.familyId, value.recommendedValue, [
            "recommendedValue"
        ]);
    }
});
export const SpecPolicyFlagHistoryEntrySchema = z
    .object({
    flagId: IdSchema,
    value: SpecPolicyFlagValueSchema,
    provenance: SpecPolicyFlagProvenanceSchema,
    why: NonEmptyStringSchema,
    confidence: SpecPolicyFlagConfidenceSchema,
    evidenceRefs: z.array(SourceRefSchema).optional(),
    defaultProfileId: IdSchema.optional(),
    defaultProfileVersion: PositiveIntegerSchema.optional(),
    priorValueFingerprint: NonEmptyStringSchema.optional(),
    newValueFingerprint: NonEmptyStringSchema,
    flagSetVersion: PositiveIntegerSchema,
    catalogVersion: NonEmptyStringSchema,
    schemaVersion: NonEmptyStringSchema,
    overrideReason: NonEmptyStringSchema.optional(),
    createdByJobId: IdSchema.optional(),
    recheckJobId: IdSchema.optional(),
    regenerateJobId: IdSchema.optional(),
    actor: z
        .object({
        type: z.enum(["ai", "user", "system"]),
        id: IdSchema.optional(),
        label: NonEmptyStringSchema.optional()
    })
        .strict()
        .optional(),
    decidedAt: IsoTimestampSchema
})
    .strict()
    .superRefine((value, ctx) => {
    requireEvidenceForNonDefault(ctx, value.provenance, value.evidenceRefs, [
        "evidenceRefs"
    ]);
});
export const SpecPolicyFlagSchema = z
    .object({
    id: IdSchema,
    familyId: SpecPolicyFlagFamilyIdSchema,
    domain: SpecPolicyFlagDomainSchema,
    scope: SpecPolicyFlagScopeSchema,
    value: SpecPolicyFlagValueSchema,
    humanName: NonEmptyStringSchema,
    plainDescription: NonEmptyStringSchema,
    impactSummary: NonEmptyStringSchema,
    evidenceRefs: z.array(SourceRefSchema),
    why: NonEmptyStringSchema,
    fieldReasons: z.record(z.string(), NonEmptyStringSchema).optional(),
    conflict: SpecPolicyFlagConflictSchema.optional(),
    confidence: SpecPolicyFlagConfidenceSchema,
    provenance: SpecPolicyFlagProvenanceSchema,
    status: z.enum(["active", "superseded"]),
    defaultProfileId: IdSchema.optional(),
    defaultProfileVersion: PositiveIntegerSchema.optional(),
    priorFlagId: IdSchema.optional(),
    priorValue: SpecPolicyFlagValueSchema.optional(),
    history: z.array(SpecPolicyFlagHistoryEntrySchema),
    extractedAt: IsoTimestampSchema.optional(),
    decidedAt: IsoTimestampSchema,
    appliedAt: IsoTimestampSchema.optional(),
    supersededAt: IsoTimestampSchema.optional(),
    updatedAt: IsoTimestampSchema
})
    .strict()
    .superRefine((value, ctx) => {
    addFamilyValueIssues(ctx, value.familyId, value.value, ["value"]);
    if (value.priorValue !== undefined) {
        addFamilyValueIssues(ctx, value.familyId, value.priorValue, ["priorValue"]);
    }
    value.history.forEach((historyEntry, index) => {
        addFamilyValueIssues(ctx, value.familyId, historyEntry.value, [
            "history",
            index,
            "value"
        ]);
    });
    if (value.domain !== SpecPolicyFlagFamilyDomains[value.familyId]) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "policy flag domain must match familyId",
            path: ["domain"]
        });
    }
    if (value.provenance === "org-default") {
        if (!value.defaultProfileId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "org-default policy flags require defaultProfileId",
                path: ["defaultProfileId"]
            });
        }
        if (value.defaultProfileVersion === undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "org-default policy flags require defaultProfileVersion",
                path: ["defaultProfileVersion"]
            });
        }
        if (value.evidenceRefs.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "org-default policy flags must not carry tender evidenceRefs",
                path: ["evidenceRefs"]
            });
        }
    }
    else if (value.defaultProfileId !== undefined ||
        value.defaultProfileVersion !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "defaultProfileId/defaultProfileVersion may only appear on org-default policy flags",
            path: ["defaultProfileId"]
        });
    }
    if (value.provenance === "provider-conflict") {
        if (!value.conflict) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "provider-conflict policy flags require structured conflict data",
                path: ["conflict"]
            });
        }
    }
    else if (value.conflict !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "structured conflict data may only appear on provider-conflict flags",
            path: ["conflict"]
        });
    }
    if (value.conflict !== undefined) {
        if (value.conflict.familyId !== value.familyId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "structured conflict familyId must match policy flag familyId",
                path: ["conflict", "familyId"]
            });
        }
        if (stableUnknownStringify(value.conflict.scope) !==
            stableUnknownStringify(value.scope)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "structured conflict scope must match policy flag scope",
                path: ["conflict", "scope"]
            });
        }
    }
    requireEvidenceForNonDefault(ctx, value.provenance, value.evidenceRefs, [
        "evidenceRefs"
    ]);
    addLabelledDefaultProvenanceLabelIssues(ctx, value.familyId, value.provenance, value.why, ["why"]);
    addDerivableConcreteValueProvenanceIssues(ctx, value.familyId, value.value, value.provenance, value.evidenceRefs, value.why, ["value", derivableIssuePathSegment(value.value)]);
    value.history.forEach((historyEntry, index) => {
        addDerivableConcreteValueProvenanceIssues(ctx, value.familyId, historyEntry.value, historyEntry.provenance, historyEntry.evidenceRefs, historyEntry.why, ["history", index, "value", derivableIssuePathSegment(historyEntry.value)]);
        addLabelledDefaultProvenanceLabelIssues(ctx, value.familyId, historyEntry.provenance, historyEntry.why, ["history", index, "why"]);
    });
});
export const SpecPolicyActiveValueVectorEntrySchema = z
    .object({
    familyId: SpecPolicyFlagFamilyIdSchema,
    scope: SpecPolicyFlagScopeSchema,
    value: SpecPolicyFlagValueSchema,
    derivedMaterialResults: z.record(z.string(), SpecPolicyFlagValueSchema).optional()
})
    .strict()
    .superRefine((value, ctx) => {
    addFamilyValueIssues(ctx, value.familyId, value.value, ["value"]);
});
export const SpecPolicyActiveValueVectorSchema = z.array(SpecPolicyActiveValueVectorEntrySchema);
export const SpecPolicyFlagSetSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    version: PositiveIntegerSchema,
    catalogVersion: NonEmptyStringSchema,
    schemaVersion: NonEmptyStringSchema,
    activeFlags: z.array(SpecPolicyFlagSchema),
    supersededFlags: z.array(SpecPolicyFlagSchema),
    canonicalActiveValueVector: SpecPolicyActiveValueVectorSchema,
    valueFingerprint: NonEmptyStringSchema,
    explanationFingerprint: NonEmptyStringSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
})
    .strict()
    .superRefine((value, ctx) => {
    value.activeFlags.forEach((flag, index) => {
        if (flag.status !== "active") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "activeFlags may contain only active flags",
                path: ["activeFlags", index, "status"]
            });
        }
    });
    value.supersededFlags.forEach((flag, index) => {
        if (flag.status !== "superseded") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "supersededFlags may contain only superseded flags",
                path: ["supersededFlags", index, "status"]
            });
        }
    });
    addCanonicalActiveValueVectorConsistencyIssues(value, ctx);
});
export const SpecPolicyFlagProposalSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    proposedFlags: z.array(SpecPolicyFlagSchema),
    conflictFlags: z.array(SpecPolicyFlagConflictSchema),
    defaultedFlags: z.array(SpecPolicyFlagSchema),
    createdByJobId: IdSchema.optional(),
    proposedAt: IsoTimestampSchema
})
    .strict();
export const FlagDiscoveryGroupIdSchema = z.enum([
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H"
]);
export const FlagDiscoveryClusterSchema = z
    .object({
    governingRequirementTextHash: NonEmptyStringSchema,
    takeoffFeatureSignature: z.record(z.string(), z.union([z.string(), z.boolean()])),
    memberApartmentTypeIds: z.array(IdSchema).min(1)
})
    .strict();
const FlagDiscoveryProposedResultSchema = z
    .object({
    kind: z.literal("proposed"),
    value: SpecPolicyFlagValueSchema,
    evidenceRefs: z.array(SourceRefSchema).min(1),
    why: NonEmptyStringSchema,
    confidence: SpecPolicyFlagConfidenceSchema
})
    .strict();
const FlagDiscoveryDefaultedResultSchema = z
    .object({
    kind: z.literal("defaulted"),
    defaultValue: SpecPolicyFlagValueSchema,
    why: z.literal("default — no trigger found in spec"),
    confidence: SpecPolicyFlagConfidenceSchema
})
    .strict();
const FlagDiscoveryConflictResultSchema = z
    .object({
    kind: z.literal("conflict"),
    conflict: SpecPolicyFlagConflictSchema
})
    .strict();
export const FlagDiscoveryFamilyResultSchema = z
    .object({
    familyId: SpecPolicyFlagFamilyIdSchema,
    result: z.discriminatedUnion("kind", [
        FlagDiscoveryProposedResultSchema,
        FlagDiscoveryDefaultedResultSchema,
        FlagDiscoveryConflictResultSchema
    ])
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.result.kind === "proposed") {
        addFamilyValueIssues(ctx, value.familyId, value.result.value, [
            "result",
            "value"
        ]);
    }
    if (value.result.kind === "defaulted") {
        addFamilyValueIssues(ctx, value.familyId, value.result.defaultValue, [
            "result",
            "defaultValue"
        ]);
        addDerivableDefaultValueIssues(ctx, value.familyId, value.result.defaultValue, [
            "result",
            "defaultValue",
            derivableIssuePathSegment(value.result.defaultValue)
        ]);
    }
    if (value.result.kind === "conflict" &&
        value.result.conflict.familyId !== value.familyId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "conflict familyId must match family result familyId",
            path: ["result", "conflict", "familyId"]
        });
    }
});
export const FlagDiscoveryProviderResponseSchema = z
    .object({
    groupId: FlagDiscoveryGroupIdSchema,
    scope: SpecPolicyFlagScopeSchema,
    cluster: FlagDiscoveryClusterSchema.optional(),
    familyResults: z.array(FlagDiscoveryFamilyResultSchema)
})
    .strict()
    .superRefine((value, ctx) => {
    // F5 completeness contract (`_build/REVIEW-F2-FLAGS-DEMO.md` F3/F5): a
    // provider response naming the same familyId twice is malformed input —
    // the resolver materializes exactly one flag per family, so a duplicate
    // would silently discard one candidate. Reject it here so the Gemini path
    // retries through the format-retry loop instead of the resolver quietly
    // picking a winner.
    const seenAt = new Map();
    value.familyResults.forEach((familyResult, index) => {
        const firstIndex = seenAt.get(familyResult.familyId);
        if (firstIndex !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `duplicate familyId "${familyResult.familyId}" in discovery response familyResults (first seen at index ${firstIndex})`,
                path: ["familyResults", index, "familyId"]
            });
            return;
        }
        seenAt.set(familyResult.familyId, index);
    });
});
const SpecPolicyDefaultScopeTemplateSchema = z.discriminatedUnion("level", [
    z.object({ level: z.literal("project") }).strict(),
    z.object({ level: z.literal("apartment-type") }).strict(),
    z
        .object({
        level: z.literal("room"),
        roomKind: NonEmptyStringSchema.optional()
    })
        .strict(),
    z
        .object({
        level: z.literal("item-class"),
        itemClass: NonEmptyStringSchema
    })
        .strict()
]);
export const SpecPolicyDefaultSchema = z
    .object({
    id: IdSchema,
    familyId: SpecPolicyFlagFamilyIdSchema,
    scopeTemplate: SpecPolicyDefaultScopeTemplateSchema,
    value: SpecPolicyFlagValueSchema,
    why: NonEmptyStringSchema,
    appliesWhen: z.literal("tender-silent-only"),
    enabled: z.boolean()
})
    .strict()
    .superRefine((value, ctx) => {
    addFamilyValueIssues(ctx, value.familyId, value.value, ["value"]);
    addDerivableDefaultValueIssues(ctx, value.familyId, value.value, [
        "value",
        derivableIssuePathSegment(value.value)
    ]);
});
export const SpecPolicyDefaultProfileSchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema,
    ownerUserId: IdSchema.optional(),
    name: NonEmptyStringSchema,
    description: NonEmptyStringSchema,
    isActive: z.boolean(),
    version: PositiveIntegerSchema,
    appliesTo: z
        .object({
        projectKinds: z.array(NonEmptyStringSchema).optional(),
        clientIds: z.array(IdSchema).optional(),
        tenderKinds: z.array(NonEmptyStringSchema).optional()
    })
        .strict(),
    defaults: z.array(SpecPolicyDefaultSchema),
    createdBy: IdSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
})
    .strict();
export function applySpecPolicyDefaultProfileToFlagSet(input) {
    const defaultsByFamily = enabledProjectDefaultsByFamily(input.defaultProfile);
    if (defaultsByFamily.size === 0) {
        return input.flagSet;
    }
    const defaultProfile = input.defaultProfile;
    const now = input.now ?? input.flagSet.updatedAt;
    let changed = false;
    const activeFlags = input.flagSet.activeFlags.map((flag) => {
        if (flag.provenance !== "standard-default") {
            return flag;
        }
        const policyDefault = defaultsByFamily.get(flag.familyId);
        if (!policyDefault || !defaultProfile) {
            return flag;
        }
        changed = true;
        return {
            ...flag,
            value: policyDefault.value,
            evidenceRefs: [],
            why: policyDefault.why || SPEC_POLICY_FLAG_ORG_DEFAULT_LABEL,
            confidence: { score: 0.65, label: "medium" },
            provenance: "org-default",
            defaultProfileId: defaultProfile.id,
            defaultProfileVersion: defaultProfile.version,
            fieldReasons: {
                ...flag.fieldReasons,
                orgDefault: `No tender trigger was found, so org default profile "${defaultProfile.name}" v${defaultProfile.version.toString()} applied default "${policyDefault.id}".`
            },
            appliedAt: now,
            updatedAt: now
        };
    });
    if (!changed) {
        return input.flagSet;
    }
    return rebuildSpecPolicyFlagSet({
        existing: input.flagSet,
        activeFlags,
        now,
        version: input.flagSet.version + 1
    });
}
export function validateSpecPolicyFlagSetDefaultProfileReferences(input) {
    const defaultsByFamily = enabledProjectDefaultsByFamily(input.defaultProfile);
    const errors = [];
    input.flagSet.activeFlags.forEach((flag) => {
        const hasProfileReference = flag.defaultProfileId !== undefined || flag.defaultProfileVersion !== undefined;
        if (!hasProfileReference && flag.provenance !== "org-default") {
            return;
        }
        if (flag.provenance !== "org-default") {
            errors.push(`${flag.familyId} carries default profile metadata but provenance is ${flag.provenance}; default profiles may only apply as org-default.`);
            return;
        }
        if (!input.defaultProfile) {
            errors.push(`${flag.familyId} references org default profile ${flag.defaultProfileId ?? "(missing)"}, but no active profile exists.`);
            return;
        }
        if (flag.defaultProfileId !== input.defaultProfile.id ||
            flag.defaultProfileVersion !== input.defaultProfile.version) {
            errors.push(`${flag.familyId} references org default profile ${flag.defaultProfileId ?? "(missing)"} v${flag.defaultProfileVersion?.toString() ?? "(missing)"}, but the active profile is ${input.defaultProfile.id} v${input.defaultProfile.version.toString()}.`);
            return;
        }
        if (flag.evidenceRefs.length > 0) {
            errors.push(`${flag.familyId} is org-default but carries tender evidence; org defaults may apply only to tender-silent flags.`);
        }
        const policyDefault = defaultsByFamily.get(flag.familyId);
        if (!policyDefault) {
            errors.push(`${flag.familyId} references active org default profile ${input.defaultProfile.id}, but that profile has no enabled project default for the family.`);
            return;
        }
        if (stableUnknownStringify(flag.value) !== stableUnknownStringify(policyDefault.value)) {
            errors.push(`${flag.familyId} org-default value does not match active profile default ${policyDefault.id}.`);
        }
    });
    return errors;
}
export function validateSpecPolicyFlagSetProvenanceTransitions(input) {
    if (!input.existingFlagSet) {
        return [];
    }
    const protectedFamilies = new Map();
    for (const flag of input.existingFlagSet.activeFlags) {
        if (!isDefaultOverwrittenProtectedProvenance(flag.provenance)) {
            continue;
        }
        protectedFamilies.set(flag.familyId, flag.provenance);
    }
    if (protectedFamilies.size === 0) {
        return [];
    }
    const reportedFamilies = new Set();
    const errors = [];
    const nextFamilies = new Set();
    for (const nextFlag of input.nextFlagSet.activeFlags) {
        nextFamilies.add(nextFlag.familyId);
        const existingProvenance = protectedFamilies.get(nextFlag.familyId);
        if (existingProvenance === undefined ||
            reportedFamilies.has(nextFlag.familyId) ||
            !isDefaultPolicyFlagProvenance(nextFlag.provenance)) {
            continue;
        }
        reportedFamilies.add(nextFlag.familyId);
        errors.push(`${nextFlag.familyId} cannot change from stored ${existingProvenance} provenance to ${nextFlag.provenance}; tender-detected and user-decided policy flags require fresh discovery or explicit user-decision provenance, not a default-profile overwrite.`);
    }
    for (const [familyId, existingProvenance] of protectedFamilies) {
        if (reportedFamilies.has(familyId) || nextFamilies.has(familyId)) {
            continue;
        }
        reportedFamilies.add(familyId);
        errors.push(`${familyId} cannot be omitted from the next policy flag set while stored ${existingProvenance} provenance is protected; tender-detected and user-decided policy flags require fresh discovery or explicit user-decision provenance, not a silent drop.`);
    }
    return errors;
}
const catalogOrder = new Map(SPEC_POLICY_FLAG_FAMILY_IDS.map((familyId, index) => [familyId, index]));
const isDefaultPolicyFlagProvenance = (provenance) => provenance === "standard-default" || provenance === "org-default";
const isDefaultOverwrittenProtectedProvenance = (provenance) => provenance === "ai-extracted" ||
    provenance === "user-decision" ||
    provenance === "user-override" ||
    provenance === "provider-conflict";
const scopeLevelOrder = {
    project: 0,
    requirement: 1,
    "apartment-type": 2,
    room: 3,
    "item-class": 4
};
const scopeSortKey = (scope) => {
    switch (scope.level) {
        case "project":
            return `${scopeLevelOrder[scope.level]}:${scope.projectId}`;
        case "requirement":
            return `${scopeLevelOrder[scope.level]}:${scope.requirementId}`;
        case "apartment-type":
            return `${scopeLevelOrder[scope.level]}:${scope.apartmentTypeId}`;
        case "room":
            return `${scopeLevelOrder[scope.level]}:${scope.apartmentTypeId}:${scope.roomRef}`;
        case "item-class":
            return `${scopeLevelOrder[scope.level]}:${scope.apartmentTypeId ?? ""}:${scope.itemClass}`;
    }
    const _exhaustive = scope;
    return _exhaustive;
};
const scopeProviderLabel = (scope) => {
    switch (scope.level) {
        case "project":
            return `project:${scope.projectId}`;
        case "requirement":
            return `requirement:${scope.requirementId}`;
        case "apartment-type":
            return `apartment-type:${scope.apartmentTypeId}`;
        case "room":
            return `room:${scope.apartmentTypeId}:${scope.roomRef}`;
        case "item-class":
            return scope.apartmentTypeId === undefined
                ? `item-class:${scope.itemClass}`
                : `item-class:${scope.apartmentTypeId}:${scope.itemClass}`;
    }
};
const sortedActiveValueVector = (vector) => [...vector].sort((left, right) => {
    const leftFamilyOrder = catalogOrder.get(left.familyId) ?? Number.MAX_SAFE_INTEGER;
    const rightFamilyOrder = catalogOrder.get(right.familyId) ?? Number.MAX_SAFE_INTEGER;
    if (leftFamilyOrder !== rightFamilyOrder) {
        return leftFamilyOrder - rightFamilyOrder;
    }
    const scopeComparison = scopeSortKey(left.scope).localeCompare(scopeSortKey(right.scope));
    if (scopeComparison !== 0) {
        return scopeComparison;
    }
    return stableUnknownStringify(left.value).localeCompare(stableUnknownStringify(right.value));
});
const activeValueVectorIdentityKey = (entry) => [
    entry.familyId,
    stableUnknownStringify(entry.scope),
    stableUnknownStringify(entry.value)
].join("\u0000");
function enabledProjectDefaultsByFamily(defaultProfile) {
    const defaults = new Map();
    if (!defaultProfile?.isActive) {
        return defaults;
    }
    for (const policyDefault of defaultProfile.defaults) {
        if (!policyDefault.enabled ||
            policyDefault.appliesWhen !== "tender-silent-only" ||
            policyDefault.scopeTemplate.level !== "project") {
            continue;
        }
        defaults.set(policyDefault.familyId, policyDefault);
    }
    return defaults;
}
function rebuildSpecPolicyFlagSet(input) {
    const activeFlags = sortSpecPolicyFlags(input.activeFlags);
    const canonicalActiveValueVector = sortedActiveValueVector(activeFlags.map((flag) => ({
        familyId: flag.familyId,
        scope: flag.scope,
        value: flag.value
    })));
    return SpecPolicyFlagSetSchema.parse({
        ...input.existing,
        version: input.version,
        schemaVersion: SPEC_POLICY_FLAGS_SCHEMA_VERSION,
        activeFlags,
        canonicalActiveValueVector,
        valueFingerprint: specPolicyStableFingerprint(canonicalActiveValueVector),
        explanationFingerprint: specPolicyStableFingerprint(activeFlags.map((flag) => ({
            familyId: flag.familyId,
            provenance: flag.provenance,
            why: flag.why,
            defaultProfileId: flag.defaultProfileId ?? "",
            defaultProfileVersion: flag.defaultProfileVersion ?? 0,
            conflictReason: flag.conflict?.reason ?? "",
            evidenceRefs: flag.evidenceRefs.map((sourceRef) => ({
                sourceId: sourceRef.sourceId,
                locator: sourceRef.locator,
                quote: sourceRef.quote ?? ""
            }))
        }))),
        updatedAt: input.now
    });
}
function sortSpecPolicyFlags(flags) {
    return [...flags].sort((left, right) => {
        const leftFamilyOrder = catalogOrder.get(left.familyId) ?? Number.MAX_SAFE_INTEGER;
        const rightFamilyOrder = catalogOrder.get(right.familyId) ?? Number.MAX_SAFE_INTEGER;
        if (leftFamilyOrder !== rightFamilyOrder) {
            return leftFamilyOrder - rightFamilyOrder;
        }
        return scopeSortKey(left.scope).localeCompare(scopeSortKey(right.scope));
    });
}
function specPolicyStableFingerprint(value) {
    return `fnv1a64:${fnv1a64Hex(stableUnknownStringify(value))}`;
}
function fnv1a64Hex(value) {
    let hash = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    const mask = 0xffffffffffffffffn;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= BigInt(value.charCodeAt(index));
        hash = (hash * prime) & mask;
    }
    return hash.toString(16).padStart(16, "0");
}
const isCanonicalActiveValueVectorOrder = (vector) => {
    const sortedVector = sortedActiveValueVector(vector);
    return vector.every((entry, index) => {
        const sortedEntry = sortedVector[index];
        return (sortedEntry !== undefined &&
            activeValueVectorIdentityKey(entry) === activeValueVectorIdentityKey(sortedEntry));
    });
};
const addCanonicalActiveValueVectorConsistencyIssues = (value, ctx) => {
    if (value.activeFlags.length !== value.canonicalActiveValueVector.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "canonicalActiveValueVector must contain exactly one entry per active flag",
            path: ["canonicalActiveValueVector"]
        });
    }
    if (!isCanonicalActiveValueVectorOrder(value.canonicalActiveValueVector)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "canonicalActiveValueVector must be ordered by catalog family, scope, and value fingerprint",
            path: ["canonicalActiveValueVector"]
        });
    }
    const remainingVectorIndexesByKey = new Map();
    value.canonicalActiveValueVector.forEach((entry, index) => {
        const key = activeValueVectorIdentityKey(entry);
        const existingIndexes = remainingVectorIndexesByKey.get(key);
        if (existingIndexes === undefined) {
            remainingVectorIndexesByKey.set(key, [index]);
            return;
        }
        existingIndexes.push(index);
    });
    value.activeFlags.forEach((flag, index) => {
        const key = activeValueVectorIdentityKey({
            familyId: flag.familyId,
            scope: flag.scope,
            value: flag.value
        });
        const matchingIndexes = remainingVectorIndexesByKey.get(key);
        if (matchingIndexes !== undefined && matchingIndexes.length > 0) {
            matchingIndexes.shift();
            return;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "active flag is missing from canonicalActiveValueVector by familyId, scope, and value fingerprint",
            path: ["activeFlags", index, "value"]
        });
    });
    for (const vectorIndexes of remainingVectorIndexesByKey.values()) {
        for (const index of vectorIndexes) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "canonicalActiveValueVector entry has no matching active flag by familyId, scope, and value fingerprint",
                path: ["canonicalActiveValueVector", index]
            });
        }
    }
};
const serializeValueLines = (value, indent) => {
    switch (value.kind) {
        case "boolean":
            return [`${indent}kind: boolean`, `${indent}value: ${String(value.value)}`];
        case "mode":
            return [`${indent}kind: mode`, `${indent}mode: ${value.mode}`];
        case "integer":
            return [
                `${indent}kind: integer`,
                `${indent}value: ${String(value.value)}`,
                ...(value.unit === undefined ? [] : [`${indent}unit: ${value.unit}`])
            ];
        case "range":
            return [
                `${indent}kind: range`,
                `${indent}min: ${String(value.min)}`,
                `${indent}max: ${String(value.max)}`,
                ...(value.unit === undefined ? [] : [`${indent}unit: ${value.unit}`])
            ];
        case "list":
            return [
                `${indent}kind: list`,
                `${indent}values:`,
                ...[...value.values]
                    .sort((left, right) => left.localeCompare(right))
                    .map((entry) => `${indent}  - ${entry}`)
            ];
        case "structured":
            return [
                `${indent}kind: structured`,
                `${indent}objectType: ${value.objectType}`,
                ...Object.keys(value)
                    .filter((key) => key !== "kind" && key !== "objectType")
                    .sort()
                    .map((key) => `${indent}${key}: ${stableUnknownStringify(value[key])}`)
            ];
    }
};
export const serializeSpecPolicyFlagProviderBlock = (flagSet, options = {}) => {
    const parsed = SpecPolicyFlagSetSchema.parse(flagSet);
    const activeVector = sortedActiveValueVector(parsed.canonicalActiveValueVector);
    if (activeVector.length === 0) {
        return "";
    }
    const lines = [
        "[SPEC POLICY FLAGS - LOAD-BEARING]",
        `valueFingerprint: ${parsed.valueFingerprint}`,
        "resolutionScope:",
        `  projectId: ${parsed.projectId}`,
        ...(options.apartmentTypeId === undefined
            ? []
            : [`  apartmentTypeId: ${options.apartmentTypeId}`]),
        "flags:"
    ];
    for (const entry of activeVector) {
        lines.push(`  - familyId: ${entry.familyId}`);
        lines.push(`    scope: ${scopeProviderLabel(entry.scope)}`);
        lines.push("    value:");
        lines.push(...serializeValueLines(entry.value, "      "));
        if (entry.derivedMaterialResults !== undefined) {
            lines.push("    derivedMaterialResults:");
            for (const key of Object.keys(entry.derivedMaterialResults).sort()) {
                const materialValue = entry.derivedMaterialResults[key];
                if (materialValue === undefined) {
                    continue;
                }
                lines.push(`      ${key}:`);
                lines.push(...serializeValueLines(materialValue, "        "));
            }
        }
    }
    return lines.join("\n");
};
//# sourceMappingURL=policyFlags.js.map